import { useRef, useState, useEffect } from 'react';
import {
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Spinner,
    UncontrolledTooltip,
    Badge
} from 'reactstrap';
import { FaSearch, FaEdit, FaTrash, FaUser, FaPlus, FaRegBuilding, FaUsers, FaEye, FaBuilding, FaCheck, FaFileImport } from 'react-icons/fa';
import { CLIENT_FORM_PAGE } from '../../routes/routeConstants';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { clientActions } from '../../sagas/clientSlice';
import { formatPrice } from '../../helpers/utility';
import Swal from 'sweetalert2';
import Pagination from '../Common/Pagination';

const SEARCH_DEBOUNCE_MS = 300;

const ClientList = () => {

    const navigate = useNavigate();
    const location = useLocation();

    const getClientFormPath = () => {
        if (location.pathname.startsWith('/sales-module')) {
            return '/sales-module/clients/new';
        }
        return CLIENT_FORM_PAGE;
    };

    const getClientEditPath = (client) => {
        if (location.pathname.startsWith('/sales-module')) {
            return `/sales-module/clients/${client.id}/edit`;
        }
        return CLIENT_FORM_PAGE;
    };

    // États
    const { clients, loading, totalElements, totalPages, pageSize } = useSelector(state => state.clients);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const fileInputRef = useRef(null);
    const { t } = useTranslation();
    const dispatch = useDispatch();

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(0);
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    useEffect(() => {
        dispatch(clientActions.findAll({
            query: debouncedSearchTerm,
            page: currentPage,
            size: pageSize
        }));// Appel de l'action pour récupérer les clients    
    }, [currentPage, debouncedSearchTerm, dispatch, pageSize]);

    const handleSearchChange = (value) => {
        setSearchTerm(value);
    };


    const handleDeleteClient = (clientId) => {
        Swal.fire({
            title: 'Êtes-vous sûr ?',
            text: "Le client sera désactivé et ne pourra plus être utilisé.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Oui, désactiver !',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                let payload = {
                    id: clientId,
                    onSuccess: () => {
                        Swal.fire(
                            'Inactivé !',
                            'Le client a été désactivé avec succès.',
                            'success'
                        );
                        dispatch(clientActions.findAll({
                            query: searchTerm,
                            page: currentPage,
                            size: pageSize
                        })); // Recharger la liste des clients après la suppression
                    },
                    onError: () => {
                        Swal.fire(
                            'Erreur !',
                            'Une erreur s\'est produite lors de la désactivation.',
                            'error'
                        );
                    }
                }

                dispatch(clientActions.inactivate(payload));
            }
        });
    };

    const handleActiverClient = (clientId) => {
        Swal.fire({
            title: 'Êtes-vous sûr ?',
            text: "Le client sera activé et pourra être utilisé.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Oui, activer !',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                let payload = {
                    id: clientId,
                    onSuccess: () => {
                        Swal.fire(
                            'Activé !',
                            'Le client a été activé avec succès.',
                            'success'
                        );
                        dispatch(clientActions.findAll({
                            query: searchTerm,
                            page: currentPage,
                            size: pageSize
                        })); // Recharger la liste des clients après la suppression
                    },
                    onError: () => {
                        Swal.fire(
                            'Erreur !',
                            'Une erreur s\'est produite lors de l\'activation.',
                            'error'
                        );
                    }
                }
                dispatch(clientActions.activate(payload));
            }
        });
    };

    const handleEdit = (client) => {
        const path = getClientEditPath(client);
        navigate(path, { state: { client } });
    };

    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleImportFile = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';

        if (!file) return;

        const extension = file.name.split('.').pop()?.toLowerCase();
        if (!['xlsx', 'xls'].includes(extension)) {
            Swal.fire('Format invalide', 'Veuillez importer un fichier Excel .xlsx ou .xls avec les colonnes name et type.', 'warning');
            return;
        }

        dispatch(clientActions.importClients({
            file,
            onSuccess: (result) => {
                const imported = result?.imported || 0;
                const skipped = result?.skipped || 0;
                const errors = result?.errors || [];

                Swal.fire({
                    icon: imported > 0 ? 'success' : 'warning',
                    title: 'Import clients termine',
                    html: `
                        <div class="text-start">
                            <div><strong>${imported}</strong> client(s) importe(s)</div>
                            <div><strong>${skipped}</strong> ligne(s) ignoree(s)</div>
                            ${errors.length ? `<hr/><small>${errors.slice(0, 5).join('<br/>')}</small>` : ''}
                        </div>
                    `
                });

                setCurrentPage(0);
                dispatch(clientActions.findAll({ query: searchTerm, page: 0, size: pageSize }));
            },
            onError: () => {
                Swal.fire('Erreur', "Impossible d'importer le fichier. Verifiez les colonnes name et type.", 'error');
            }
        }));
    };

    return (
        <div>
            {/* Header Section */}

            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaUsers className="text-primary me-2" />
                                {t('client.list')}
                            </h4>
                            <div className="d-flex align-items-center gap-2">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".xlsx,.xls"
                                    className="d-none"
                                    onChange={handleImportFile}
                                />
                                <Button color="success" outline onClick={handleImportClick} disabled={loading}>
                                    <FaFileImport className="mr-2" /> Importer Excel
                                </Button>
                                <Button color="primary" onClick={() => navigate(getClientFormPath())}>
                                    <FaPlus className="mr-2" /> {t('client.newClient')}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Card className="shadow-sm">
                <CardBody>
                    <div className="filter-container mb-4">
                        <div className="input-group">
                            <span className="input-group-text bg-light">
                                <FaSearch />
                            </span>
                            <input
                                type="text"
                                placeholder="Rechercher par nom, email, telephone..."
                                value={searchTerm}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                className="form-control"
                            />
                        </div>
                    </div>
                    {loading ? ( // Show spinner when loading
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">{t('common.loading')}</div>
                        </div>
                    ) : (
                        <>
                            <div className="filter-container mb-4 d-none">
                                <div className="input-group">
                                    <span className="input-group-text bg-light">
                                        <FaSearch />
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="Rechercher par nom, email, téléphone..."
                                        value={searchTerm}
                                        onChange={(e) => handleSearchChange(e.target.value)}
                                        className="form-control"
                                    />
                                </div>
                            </div>

                            <table className="table table-hover table-striped table-bordered" >
                                <thead className="thead-light">
                                    <tr>
                                        <th className="align-middle text-center">#</th>
                                        <th className="align-middle text-center">{t('client.code')}</th>
                                        <th className="align-middle text-center">{t('client.nom')}</th>
                                        <th className="align-middle text-center">{t('fournisseur.contact')}</th>
                                        <th className="align-middle text-center">{t('client.totalDebit')}</th>
                                        <th className="align-middle text-center">{t('client.totalCredit')}</th>
                                        <th className="align-middle text-center">{t('client.solde')}</th>
                                        <th className="align-middle text-center">{t('client.active')}</th>
                                        <th className="align-middle text-center">{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {clients.map((client, index) => (
                                        <tr key={client.id}>
                                            <td className="text-center">
                                                {currentPage * pageSize + index + 1}
                                            </td>
                                            <td>
                                                <strong>{client.code}</strong>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <div className="me-3">
                                                        {client.typeClient === 'PARTICULIER' ? (
                                                            <FaUser className="text-primary" />
                                                        ) : (
                                                            <FaRegBuilding className="text-info" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="fw-bold">
                                                            {client.typeClient === 'PARTICULIER'
                                                                ? `${client.nom} ${client.prenom}`
                                                                : client.entreprise}
                                                        </div>
                                                        <small className="text-muted">
                                                            {client.typeClient === 'PARTICULIER' ? 'Particulier' : 'Entreprise'}
                                                        </small>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                
                                                    <div>
                                                        <div className="fw-bold">
                                                            {client.telephone1}
                                                        </div>
                                                        <small className="text-muted">
                                                            {client.telephone2 || client.email || '-' }
                                                        </small>
                                                    </div>
                                                </div>
                                            </td>
                                            
                                            <td>
                                                <span className="fw-bold">
                                                    {formatPrice(client.totalDebit || 0)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="fw-bold">
                                                    {formatPrice(client.totalCredit || 0)}
                                                </span>
                                            </td>
                                            <td>
                                                {Number(client.solde) < 0 ? (
                                                    <span className="text-success fw-bold">+{formatPrice(Math.abs(client.solde))}</span>
                                                ) : Number(client.solde) > 0 ? (
                                                    <span className="text-danger fw-bold">-{formatPrice(Math.abs(client.solde))}</span>
                                                ) : (
                                                    <span className="text-dark fw-bold">{formatPrice(0)}</span>
                                                )}
                                            </td>
                                            <td>
                                                {client.active ? (
                                                    <Badge color="success">Actif</Badge>
                                                ) : (
                                                    <Badge color="danger">Bloqué</Badge>
                                                )}
                                            </td>
                                            <td>

                                                <Button
                                                    color="warning"
                                                    size="sm"
                                                    onClick={() => handleEdit(client)}
                                                >
                                                    <FaEdit />
                                                </Button>
                                                {client.active ? <Button
                                                    className="ms-1"
                                                    color="danger"
                                                    title='Désactiver'
                                                    size="sm"
                                                    onClick={() => handleDeleteClient(client.id)}
                                                >
                                                    <FaTrash />
                                                </Button>
                                                    :
                                                <Button
                                                    className="ms-1"
                                                    color="success"
                                                    size="sm"
                                                    title='Activer'
                                                    onClick={() => handleActiverClient(client.id)}
                                                >
                                                    <FaCheck />
                                                </Button>
                                                }
                                            </td>
                                        </tr>
                                    ))}

                                    {clients.length === 0 && (
                                        <tr>
                                            <td colSpan="9" className="text-center py-4">
                                                <div className="text-muted">{t('common.noClient')}</div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                                <small className="text-muted">
                                    {totalElements > 0
                                        ? `${currentPage * pageSize + 1}-${Math.min((currentPage + 1) * pageSize, totalElements)} sur ${totalElements}`
                                        : '0 client'}
                                </small>
                                <Pagination
                                    totalPages={totalPages || 0}
                                    currentPage={currentPage}
                                    onPageChange={setCurrentPage}
                                />
                            </div>
                        </>
                    )}
                </CardBody>
            </Card>
        </div>
    );
};

export default ClientList;
